/**
 * Mac App Store URL — single source of truth (S5-T4.1).
 *
 * The Apple-assigned App ID and slug are set. The listing is not public yet
 * (the app is awaiting release under Starkly Pty Ltd), so these URLs will 404
 * until the listing goes live. The values themselves are final.
 *
 * COUNTRY anchors the unsigned-in storefront only; Apple redirects each
 * visitor to their own regional store. Kept "au" to match the AU-first
 * pricing on the page (.impeccable.md).
 */

const APP_STORE_ID = "6776380705";
const APP_NAME_SLUG = "imprint-bates-pdf-stamper";
const COUNTRY = "au"; // AU-first per .impeccable.md currency decision

/**
 * Media type from the campaign link App Store Connect generated for Imprint.
 * That link uses mt=8, same as Apple's campaign-link example.
 */
const MAC_APP_STORE_MEDIA_TYPE = "8";

/**
 * Provider token (`pt`) from App Store Connect. Issued once for the developer
 * account and reused on every campaign. Taken from the generated link
 * `?pt=117778230&ct=tiktok&mt=8`.
 */
const APP_STORE_PROVIDER_TOKEN = "117778230";

const REF_STORAGE_KEY = "imprint.ref";
const CAMPAIGN_REF_MAX = 30;

export const MAC_APP_STORE_WEB_URL = `https://apps.apple.com/${COUNTRY}/app/${APP_NAME_SLUG}/id${APP_STORE_ID}`;

export const MAC_APP_STORE_DEEPLINK = `macappstore://apps.apple.com/${COUNTRY}/app/${APP_NAME_SLUG}/id${APP_STORE_ID}`;

/**
 * Lowercase, keep [a-z0-9-], cap at 30 (App Store campaign name limit).
 * Anything else is dropped. Empty after that means "no ref".
 */
export function sanitizeCampaignRef(value: string | null | undefined): string | null {
  if (value == null) return null;
  const cleaned = value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, CAMPAIGN_REF_MAX);
  return cleaned === "" ? null : cleaned;
}

/**
 * A `ref` query param wins over a previously stored one. An invalid `ref`
 * is ignored, and the stored value is used instead.
 */
export function readCampaignRef(search: string, stored: string | null): string | null {
  const params = new URLSearchParams(search);
  if (params.has("ref")) {
    const fromUrl = sanitizeCampaignRef(params.get("ref"));
    if (fromUrl) return fromUrl;
  }
  return sanitizeCampaignRef(stored);
}

/**
 * Append App Store Connect campaign params. No ref returns `url` unchanged.
 * `pt` is included only when a provider token is set.
 */
export function appStoreUrlWithCampaign(
  url: string,
  ref: string | null,
  providerToken = APP_STORE_PROVIDER_TOKEN,
): string {
  const campaign = sanitizeCampaignRef(ref);
  if (!campaign) return url;
  const params = new URLSearchParams();
  if (providerToken) params.set("pt", providerToken);
  params.set("ct", campaign);
  params.set("mt", MAC_APP_STORE_MEDIA_TYPE);
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}${params.toString()}`;
}

function visitCampaignRef(): string | null {
  if (typeof window === "undefined") return null;
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(REF_STORAGE_KEY);
  } catch {
    stored = null;
  }
  const ref = readCampaignRef(window.location.search, stored);
  if (!ref) return null;
  try {
    window.sessionStorage.setItem(REF_STORAGE_KEY, ref);
  } catch {
    // sessionStorage can throw in private mode. The URL ref still applies
    // on this page; it just will not survive a later navigation.
  }
  return ref;
}

/**
 * `macappstore://` on macOS (opens the App Store app directly), https://
 * web URL elsewhere. iOS/iPadOS visitors fall through to the web URL since
 * Imprint is Mac-only and an iOS deeplink would surface a "can't open"
 * sheet.
 *
 * When the visit arrived with `?ref=`, that value is the campaign token
 * (`ct`) on every App Store link for the rest of the tab.
 */
export function getMacAppStoreHref(): string {
  const base =
    typeof navigator === "undefined"
      ? MAC_APP_STORE_WEB_URL
      : /Macintosh/.test(navigator.userAgent)
        ? MAC_APP_STORE_DEEPLINK
        : MAC_APP_STORE_WEB_URL;
  return appStoreUrlWithCampaign(base, visitCampaignRef());
}
